import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  FormControlLabel,
  Tooltip,
  Grid,
  Chip,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../store/Store";
import {
  fetchData,
  postData,
  putData,
  deleteData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  processComplianceTemplatesUrl,
  sendComplianceNotificationsUrl,
} from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import ConfirmDialog from "../../components/Dialogs/ConfirmDialog";
import PageTitle from "../../components/Page Component/PageTitle";
import { getDefaultQuestions } from "./utils";
import { getDataGridPaginationProps } from "utils/paginationConfig";

// Updated interfaces for the sprint-based structure

interface SprintName {
  id: string;
  name: string;
}

interface Question {
  id: string;
  question: string;
  department?: string; // Optional department field
}

interface Department {
  id: string;
  name: string;
  percentage: number; // Weight percentage for this department
}

interface TemplateData {
  questions: Question[];
  sprintCount: number;
  sprintNames?: SprintName[];
  departments: Department[]; // Add departments array
}

interface Template {
  template_id: string;
  template_name: string;
  template_data: TemplateData;
  is_active: boolean;
  created_on: string;
  updated_on: string;
}

interface NotificationResponse {
  success: boolean;
  notifications_sent: number;
  message?: string;
}

const ProcessComplianceTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [formData, setFormData] = useState<Partial<Template>>({
    template_name: "",
    is_active: true,
    template_data: {
      questions: [],
      sprintCount: 1, // Default to 4 sprints
      sprintNames: [],
      departments: [],
    },
  });

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(`${processComplianceTemplatesUrl}?role=${selectedRole}`)
      );
      setTemplates(response as Template[]);
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast("Error fetching templates", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleOpenDialog = (template?: Template) => {
    if (template) {
      setEditMode(true);
      setSelectedTemplate(template);

      // Make sure sprintNames exist or create them
      let sprintNames = template.template_data.sprintNames || [];
      if (!sprintNames.length) {
        sprintNames = Array.from(
          { length: template.template_data.sprintCount || 4 },
          (_, i) => ({ id: `sprint-${i + 1}`, name: `Sprint ${i + 1}` })
        );
      }

      setFormData({
        template_name: template.template_name,
        is_active: template.is_active,
        template_data: {
          ...JSON.parse(JSON.stringify(template.template_data)), // Deep copy
          sprintNames,
        },
      });
    } else {
      setEditMode(false);
      setSelectedTemplate(null);

      // Initialize with default questions and sprint names
      setFormData({
        template_name: "",
        is_active: true,
        template_data: {
          questions: getDefaultQuestions(),
          sprintCount: 4,
          sprintNames: Array.from({ length: 4 }, (_, i) => ({
            id: `sprint-${i + 1}`,
            name: `Sprint ${i + 1}`,
          })),
          departments: getDefaultDepartments(), // Add this
        },
      });
    }
    setOpenDialog(true);
  };
  const getDefaultDepartments = (): Department[] => {
    return [
      { id: "dept-1", name: "Sprint Planning", percentage: 20 },
      { id: "dept-2", name: "Development & Testing", percentage: 70 },
      { id: "dept-3", name: "Deployment", percentage: 9 },
      { id: "dept-4", name: "Review and Retrospective", percentage: 1 },
    ];
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAddDepartment = () => {
    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        departments: [
          ...formData.template_data!.departments,
          { id: `dept-${Date.now().toString()}`, name: "", percentage: 0 },
        ],
      },
    });
  };

  const handleUpdateDepartment = (
    index: number,
    field: "name" | "percentage",
    value: string | number
  ) => {
    const updatedDepartments = [...formData.template_data!.departments];
    updatedDepartments[index] = {
      ...updatedDepartments[index],
      [field]: value,
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        departments: updatedDepartments,
      },
    });
  };

  const handleRemoveDepartment = (index: number) => {
    const updatedDepartments = [...formData.template_data!.departments];
    const departmentId = updatedDepartments[index].id;
    updatedDepartments.splice(index, 1);

    // Update questions that belonged to this department
    const updatedQuestions = formData.template_data!.questions.map((q) => {
      if (q.department === departmentId) {
        return { ...q, department: undefined };
      }
      return q;
    });

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        departments: updatedDepartments,
        questions: updatedQuestions,
      },
    });
  };

  const handleAssignQuestionDepartment = (
    questionIndex: number,
    departmentId: string
  ) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      department: departmentId,
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    if (name === "is_active") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleAddQuestion = () => {
    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: [
          ...formData.template_data!.questions,
          { id: `q-${Date.now().toString()}`, question: "" },
        ],
      },
    });
  };

  const handleUpdateQuestion = (index: number, value: string) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question: value,
    };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...formData.template_data!.questions];
    updatedQuestions.splice(index, 1);

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        questions: updatedQuestions,
      },
    });
  };

  const handleSprintCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (isNaN(value) || value < 1) return;

    // Update sprint count and adjust sprint names array accordingly
    const sprintCount = value;
    const oldSprintNames = formData.template_data?.sprintNames || [];

    // Create or adjust sprint names array
    const sprintNames = Array.from({ length: sprintCount }, (_, i) => {
      // Keep existing sprint names if available
      if (i < oldSprintNames.length) {
        return oldSprintNames[i];
      }
      // Create new sprint names for additional sprints
      return { id: `sprint-${i + 1}`, name: `Sprint ${i + 1}` };
    });

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data!,
        sprintCount,
        sprintNames,
      },
    });
  };

  const handleSprintNameChange = (index: number, value: string) => {
    if (!formData.template_data || !formData.template_data.sprintNames) return;

    const sprintNames = [...formData.template_data.sprintNames];
    sprintNames[index] = { ...sprintNames[index], name: value };

    setFormData({
      ...formData,
      template_data: {
        ...formData.template_data,
        sprintNames,
      },
    });
  };

  // Add this validation check in the handleSubmit function
  const handleSubmit = async () => {
    try {
      dispatch(setIsLoading(true));

      if (!formData.template_name) {
        showToast("Template name is required", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      if (
        !formData.template_data?.questions ||
        formData.template_data?.questions.length === 0
      ) {
        showToast("At least one question is required", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Check for empty questions
      const emptyQuestions = formData.template_data?.questions.some(
        (q) => !q.question.trim()
      );
      if (emptyQuestions) {
        showToast("Questions cannot be empty", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Check department percentages total 100%
      const totalPercentage = formData.template_data?.departments.reduce(
        (sum, dept) => sum + (dept.percentage || 0),
        0
      );
      if (totalPercentage !== 100) {
        showToast(
          "Department percentages must total exactly 100%",
          "error",
          true
        );
        dispatch(setIsLoading(false));
        return;
      }

      // Sprint count validation
      if (
        !formData.template_data?.sprintCount ||
        formData.template_data.sprintCount < 1
      ) {
        showToast("Sprint count must be at least 1", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      if (editMode && selectedTemplate) {
        await dispatch2(
          putData(
            `${processComplianceTemplatesUrl}${selectedTemplate.template_id}/?role=${selectedRole}`,
            formData
          )
        );
        showToast("Template updated successfully", "success", true);
      } else {
        await dispatch2(
          postData(
            `${processComplianceTemplatesUrl}?role=${selectedRole}`,
            formData
          )
        );
        showToast("Template created successfully", "success", true);
      }

      handleCloseDialog();
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      showToast("Error saving template", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Add state variables for the delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);

  // Replace your delete handler with this
  const handleDeleteTemplate = (templateId: string) => {
    setTemplateToDelete(templateId);
    setDeleteDialogOpen(true);
  };

  const executeTemplateDelete = async () => {
    if (!templateToDelete) return;

    try {
      dispatch(setIsLoading(true));
      await dispatch2(
        deleteData(
          `${processComplianceTemplatesUrl}${templateToDelete}/?role=${selectedRole}`
        )
      );
      showToast("Template deleted successfully", "success", true);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast("Error deleting template", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Add the dialog to your render function
  const renderDeleteConfirmDialog = () => (
    <ConfirmDialog
      open={deleteDialogOpen}
      title="Delete Template"
      message="Are you sure you want to delete this template? This action cannot be undone and will remove all associated data."
      severity="error"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      onConfirm={() => {
        setDeleteDialogOpen(false);
        executeTemplateDelete();
      }}
      onCancel={() => {
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
      }}
    />
  );

  const toggleTemplateStatus = async (template: Template) => {
    try {
      dispatch(setIsLoading(true));
      await dispatch2(
        putData(
          `${processComplianceTemplatesUrl}${template.template_id}/?role=${selectedRole}`,
          {
            is_active: !template.is_active,
          }
        )
      );
      showToast(
        `Template ${
          template.is_active ? "deactivated" : "activated"
        } successfully`,
        "success",
        true
      );
      fetchTemplates();
    } catch (error) {
      console.error("Error toggling template status:", error);
      showToast("Error updating template status", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleSendNotifications = async () => {
    try {
      dispatch(setIsLoading(true));

      // Cast the response to the proper type
      const response = (await dispatch2(
        postData(`${sendComplianceNotificationsUrl}?role=${selectedRole}`, {})
      )) as any;

      // Now TypeScript knows that response has 'notifications_sent' property
      if (response && response.notifications_sent > 0) {
        showToast(
          `${
            response.message ||
            `Sent ${response.notifications_sent} notifications successfully`
          }`,
          "success",
          true
        );
      } else {
        showToast("No notifications were sent", "info", true);
      }
    } catch (error) {
      console.error("Error sending notifications:", error);
      showToast("Error sending notifications", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // Default questions - these are the fixed guidelines

  const columns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr No.",
      width: 100,
      valueGetter: (params) => {
        const index = templates.findIndex(
          (t) => t.template_id === params.row.template_id
        );
        return index + 1;
      },renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Sr No.</strong>)
    },
    {
      field: "template_name",
      headerName: "Template Name",
      flex: 1,
      renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Template Name </strong>)
    },
    {
      field: "sprint_count",
      headerName: "Sprint Count",
      width: 120,
      valueGetter: (params) => params.row.template_data.sprintCount || 0
      ,renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Sprint Count</strong>)
    },
    {
      field: "questions_count",
      headerName: "Questions",
      width: 120,
      valueGetter: (params) => params.row.template_data.questions?.length || 0,
      renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Questions</strong>)
    },
    {
      field: "created_on",
      headerName: "Created On",
      width: 180,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString("en-GB");
      },renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Created On</strong>)
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Inactive"}
          size="small"
          color={params.row.is_active ? "success" : "default"}
        />
      ),renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Status</strong>)
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteTemplate(params.row.template_id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),renderHeader: () =>(<strong style={{ fontWeight: 700 }}>Actions</strong>)
    },
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle>Process Compliance Templates</PageTitle>
            <Box>
              {(selectedRole === "HR" || selectedRole === "Admin") && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<NotificationsIcon />}
                    onClick={handleSendNotifications}
                    sx={{ mr: 2 }}
                  >
                    Send Notifications
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    Add Template
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <DataGrid
            rows={templates}
            columns={columns}
            getRowId={(row) => row.template_id}
            autoHeight
            {...getDataGridPaginationProps()}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
          />
        </Paper>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "80vh" },
        }}
      >
        <DialogTitle>
          {editMode
            ? "Edit Process Compliance Template"
            : "Create Process Compliance Template"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              name="template_name"
              label="Template Name"
              value={formData.template_name}
              onChange={handleFormChange}
              fullWidth
              required
            />

            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleFormChange}
                />
              }
              label="Active"
            />

            {/* Sprint configuration */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Sprint Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Number of Sprints"
                    type="number"
                    value={formData.template_data?.sprintCount || 1}
                    onChange={handleSprintCountChange}
                    inputProps={{ min: 1, max: 12 }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography
                    variant="body2"
                    color="black"
                    gutterBottom
                  >
                    Customize sprint names (optional)
                  </Typography>
                </Grid>

                {/* Sprint name inputs */}
                {formData.template_data?.sprintNames?.map((sprint, index) => (
                  <Grid item xs={12} sm={4} key={sprint.id}>
                    <TextField
                      label={`Sprint ${index + 1} Name`}
                      value={sprint.name}
                      onChange={(e) =>
                        handleSprintNameChange(index, e.target.value)
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {/* Department configuration */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h6">Departments/Categories</Typography>
                <Button
                  variant="outlined"
                  onClick={handleAddDepartment}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Add Department
                </Button>
              </Box>

              <Typography variant="body2" color="black" gutterBottom>
                Define departments and their percentage weights (must total
                100%)
              </Typography>

              {formData.template_data?.departments.map((dept, index) => (
                <Box
                  key={dept.id}
                  display="flex"
                  gap={2}
                  mb={3}
                  alignItems="center"
                >
                  <TextField
                    label="Department Name"
                    value={dept.name}
                    onChange={(e) =>
                      handleUpdateDepartment(index, "name", e.target.value)
                    }
                    size="small"
                    sx={{ flexGrow: 2 }}
                  />
                  <TextField
                    label="Weight %"
                    value={dept.percentage}
                    onChange={(e) =>
                      handleUpdateDepartment(
                        index,
                        "percentage",
                        Number(e.target.value)
                      )
                    }
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: "100px" }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveDepartment(index)}
                    size="small"
                    disabled={formData.template_data!.departments.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {/* Weight total indicator */}
              <Box
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="body2">
                  Total weight:
                  <strong>
                    {formData.template_data?.departments.reduce(
                      (sum, dept) => sum + (dept.percentage || 0),
                      0
                    )}
                    %
                  </strong>
                </Typography>
                {formData.template_data?.departments.reduce(
                  (sum, dept) => sum + (dept.percentage || 0),
                  0
                ) !== 100 && (
                  <Chip
                    label="Total must be 100%"
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </Box>
            {/* Questions section */}
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="h6">
                  Compliance Guidelines/Questions
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleAddQuestion}
                  startIcon={<AddIcon />}
                  size="small"
                >
                  Add Question
                </Button>
              </Box>

              {/* Group questions by department */}
              {formData.template_data?.departments.map((dept) => (
                <Box key={dept.id} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ flexGrow: 1 }}
                    >
                      {dept.name} ({dept.percentage}%)
                    </Typography>
                  </Box>

                  {/* Questions for this department */}
                  {formData.template_data?.questions
                    .filter((q) => q.department === dept.id)
                    .map((question, globalIndex) => {
                      // Find the actual index in the full array
                      const actualIndex =
                        formData.template_data!.questions.findIndex(
                          (q) => q.id === question.id
                        );
                      return (
                        <Box
                          key={question.id}
                          display="flex"
                          gap={1}
                          mb={1}
                          alignItems="center"
                        >
                          <TextField
                            label={`Question`}
                            value={question.question}
                            onChange={(e) =>
                              handleUpdateQuestion(actualIndex, e.target.value)
                            }
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                          />
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={question.department || ""}
                              onChange={(e) =>
                                handleAssignQuestionDepartment(
                                  actualIndex,
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>No Department</em>
                              </MenuItem>
                              {formData.template_data?.departments.map((d) => (
                                <MenuItem key={d.id} value={d.id}>
                                  {d.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveQuestion(actualIndex)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}

                  {/* Button to add question directly to this department */}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newQuestion = {
                        id: `q-${Date.now().toString()}`,
                        question: "",
                        department: dept.id,
                      };
                      setFormData({
                        ...formData,
                        template_data: {
                          ...formData.template_data!,
                          questions: [
                            ...formData.template_data!.questions,
                            newQuestion,
                          ],
                        },
                      });
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add Question to {dept.name}
                  </Button>
                </Box>
              ))}

              {/* Questions without a department */}
              {(formData.template_data?.questions || []).filter(
                (q) => !q.department
              ).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      pb: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ flexGrow: 1 }}
                    >
                      Unassigned Questions
                    </Typography>
                  </Box>

                  {formData.template_data?.questions
                    .filter((q) => !q.department)
                    .map((question, idx) => {
                      const actualIndex =
                        formData.template_data!.questions.findIndex(
                          (q) => q.id === question.id
                        );
                      return (
                        <Box
                          key={question.id}
                          display="flex"
                          gap={1}
                          mb={1}
                          alignItems="center"
                        >
                          <TextField
                            label={`Question`}
                            value={question.question}
                            onChange={(e) =>
                              handleUpdateQuestion(actualIndex, e.target.value)
                            }
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                          />
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Department</InputLabel>
                            <Select
                              value={question.department || ""}
                              onChange={(e) =>
                                handleAssignQuestionDepartment(
                                  actualIndex,
                                  e.target.value
                                )
                              }
                              displayEmpty
                              label="Department"
                            >
                              <MenuItem value="">
                                <em>No Department</em>
                              </MenuItem>
                              {formData.template_data?.departments.map((d) => (
                                <MenuItem key={d.id} value={d.id}>
                                  {d.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveQuestion(actualIndex)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      );
                    })}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      {renderDeleteConfirmDialog()}
    </>
  );
};

export default ProcessComplianceTemplates;
